import { SPEC_FILE_NAME } from "@/constants";
import { nanoid } from "nanoid";
import { z } from "zod";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type {
  FpFile,
  FpMessage,
  FpMessagePart,
  FpVersion,
} from "../../../schemas/codegen";
import type { MessageState, VersionInformation } from "./types";

const EMPTY_MESSAGES: FpMessage[] = [];
const EPHEMERAL_VERSION_KEY = "ephemeral";

/**
 * Check if the derived version information has changed
 */
function hasVersionInformationChanged(
  versionInformationA: VersionInformation | undefined,
  versionInformationB: VersionInformation,
): boolean {
  if (!versionInformationA) {
    return true;
  }

  return (
    versionInformationA.version !== versionInformationB.version ||
    versionInformationA.files.length !== versionInformationB.files.length ||
    versionInformationA.files.some((oldFile: FpFile, index: number) => {
      const newFile = versionInformationB.files[index];
      if (!newFile) {
        return true;
      }

      return hasFileChanged(oldFile, newFile);
    }) ||
    hasFileChanged(
      versionInformationA.spec ?? undefined,
      versionInformationB.spec ?? undefined,
    )
  );
}

// Helper to extract deployment files from a message
const getDeploymentFiles = (message: FpMessage): FpFile[] | undefined => {
  // During streaming, use parts
  if (message.status === "processing" || message.status === "pending") {
    if (!message.parts) {
      return undefined;
    }

    return message.parts
      .filter(
        (part): part is Extract<FpMessagePart, { type: "tool-call" }> =>
          part.type === "tool-call" && part.toolName === "deploy",
      )
      .map((part) => ({
        id: part.id,
        filename: `Deploy Status: ${part.toolName}`,
        status: part.status === "complete" ? "completed" : "processing",
        content: part.result ? JSON.stringify(part.result) : undefined,
        createdAt: new Date(),
        updatedAt: new Date(),
      }));
  }

  // For completed messages, use version files
  return message.version?.files.filter(
    (file): file is Extract<FpFile, { filename: string }> =>
      file.filename.startsWith("Deploy Status: "),
  );
};

const FileLikeSchema = z.object({
  path: z.string(),
  content: z.string(),
});

const FileResultSuccessSchema = z.object({
  data: FileLikeSchema,
  success: z.literal(true),
});

// Type guards for message parts
const isTextPart = (
  part: FpMessagePart,
): part is Extract<FpMessagePart, { type: "text" }> => part.type === "text";
const isToolCallPart = (
  part: FpMessagePart,
): part is Extract<FpMessagePart, { type: "tool-call" }> =>
  part.type === "tool-call";

// Helper to check if deployment status has changed
const hasDeploymentStatusChanged = (
  oldFiles: FpFile[] | undefined,
  newFiles: FpFile[] | undefined,
): boolean => {
  if (!oldFiles && !newFiles) {
    return false;
  }

  if (!oldFiles || !newFiles) {
    return true;
  }

  if (oldFiles.length !== newFiles.length) {
    return true;
  }

  return oldFiles.some((oldFile, index) => {
    const newFile = newFiles[index];
    return !newFile || hasFileChanged(oldFile, newFile);
  });
};

function hasFileChanged(a: FpFile | undefined, b: FpFile | undefined): boolean {
  if (!a && !b) {
    return false;
  }
  if (!a || !b) {
    return true;
  }
  return a.id !== b.id || a.status !== b.status || a.content !== b.content;
}

function hasVersionChanged(
  versionA?: FpVersion,
  versionB?: FpVersion,
): boolean {
  if (!versionA && !versionB) {
    return false;
  }
  if (!versionA || !versionB) {
    return true;
  }

  return (
    versionA.id !== versionB.id ||
    versionA.version !== versionB.version ||
    versionA.files?.length !== versionB.files.length ||
    versionA.files.some((oldFile: FpFile) => {
      // Find the corresponding file in versionB by ID or filename
      const newFile = versionB.files.find(
        (f: FpFile) => f.id === oldFile.id || f.filename === oldFile.filename,
      );
      if (!newFile) {
        return true; // File was removed
      }

      return hasFileChanged(oldFile, newFile);
    }) ||
    // Also check if any files were added in versionB
    versionB.files.some((newFile: FpFile) => {
      const oldFile = versionA.files.find(
        (f: FpFile) => f.id === newFile.id || f.filename === newFile.filename,
      );
      return !oldFile; // File was added
    }) ||
    versionA.specId !== versionB.specId
  );
}

function generateVersionInformationFromMessages(
  messages: FpMessage[],
  oldVersions: Record<string, VersionInformation>,
): Record<string, VersionInformation> {
  let currentSpec: FpFile | null = null;
  const specMap: Record<string, FpFile> = {};
  const versionInformation: Record<string, VersionInformation> = {};
  let hasChanged = false;
  let specUsed = true;
  let lastKnownVersion = 0;
  let previousFiles: FpFile[] = [];

  // Process messages in order to track spec evolution
  for (const message of messages) {
    if (message.spec && message.status !== "cancelled") {
      specMap[message.spec.id] = message.spec;
      currentSpec = message.spec;
      specUsed = false;
    }

    if (message.version && message.status !== "cancelled") {
      if (message.version.id === EPHEMERAL_VERSION_KEY) {
        // skip ephemeral version information
        continue;
      }

      const current = oldVersions[message.version.id];
      const messageFiles = message.version.files;
      const next: VersionInformation = {
        id: message.version.id,
        version: message.version.version,
        files: [
          ...previousFiles.filter(
            (file) =>
              !messageFiles.some(
                (newFile) => newFile.filename === file.filename,
              ) && !file.filename.startsWith("Deploy Status: "),
          ),
          ...messageFiles,
        ],
        spec: specMap[message.version.specId] ?? currentSpec,
      };
      lastKnownVersion = next.version;

      if (next.spec === currentSpec) {
        specUsed = true;
      }

      if (hasVersionInformationChanged(current, next)) {
        hasChanged = true;

        versionInformation[message.version.id] = next;
      } else {
        versionInformation[message.version.id] = current;
      }

      previousFiles = next.files;
    }
  }

  // Check if the last encountered spec has not been used yet, and add an
  // ephemeral version for it
  if (currentSpec && specUsed === false) {
    const oldEphemeral = oldVersions[EPHEMERAL_VERSION_KEY];
    const ephemeralVersion: VersionInformation = {
      id: EPHEMERAL_VERSION_KEY,
      version: lastKnownVersion + 1,
      // Copy over the previous files (if there are any)
      files: previousFiles || [],
      spec: currentSpec,
    };

    if (hasVersionInformationChanged(oldEphemeral, ephemeralVersion)) {
      hasChanged = true;
      versionInformation[EPHEMERAL_VERSION_KEY] = ephemeralVersion;
    } else {
      versionInformation[EPHEMERAL_VERSION_KEY] = oldEphemeral;
    }
  }

  if (!hasChanged) {
    const currentKeys = Object.keys(versionInformation);
    const oldKeys = Object.keys(oldVersions);
    if (currentKeys.length !== oldKeys.length) {
      hasChanged = true;
    }
  }

  return hasChanged ? versionInformation : oldVersions;
}

export const useMessageStore = create<MessageState>()(
  devtools(
    (set) => ({
      messages: EMPTY_MESSAGES,
      isLoading: false,
      error: null,
      versionInformation: {},
      deploymentStatus: {},

      updateMessage: (messageId: string, update: Partial<FpMessage>): void =>
        set((state: MessageState) => {
          const messageIndex = state.messages.findIndex(
            (m: FpMessage) => m.id === messageId,
          );

          if (messageIndex === -1) {
            return state;
          }

          const updatedMessage: FpMessage = {
            ...state.messages[messageIndex],
            ...update,
          };

          const newMessages = [...state.messages];
          newMessages[messageIndex] = updatedMessage;

          // Update versions if needed
          let newVersionInformation: Record<string, VersionInformation> =
            state.versionInformation;
          const currentSpec = state.messages[messageIndex].spec;
          const updatedSpec = updatedMessage.spec;
          if (
            hasVersionChanged(
              state.messages[messageIndex].version,
              updatedMessage.version,
            ) ||
            hasFileChanged(currentSpec, updatedSpec)
          ) {
            newVersionInformation = generateVersionInformationFromMessages(
              newMessages,
              state.versionInformation,
            );
          }

          // Update deployment status only if it has changed
          const newDeploymentStatus = { ...state.deploymentStatus };
          const oldDeploymentFiles = state.deploymentStatus[messageId];
          const newDeploymentFiles = getDeploymentFiles(updatedMessage);

          if (
            hasDeploymentStatusChanged(oldDeploymentFiles, newDeploymentFiles)
          ) {
            if (newDeploymentFiles?.length) {
              newDeploymentStatus[messageId] = newDeploymentFiles;
            } else {
              delete newDeploymentStatus[messageId];
            }
          }

          return {
            ...state,
            messages: newMessages,
            versionInformation: newVersionInformation,
            deploymentStatus: newDeploymentStatus,
          };
        }),

      setMessages: (messages: FpMessage[]) =>
        set((state) => {
          // Update deployment status for all messages
          const newDeploymentStatus: Record<string, FpFile[]> = {};
          for (const message of messages) {
            const oldDeploymentFiles = state.deploymentStatus[message.id];
            const newDeploymentFiles = getDeploymentFiles(message);

            if (
              hasDeploymentStatusChanged(oldDeploymentFiles, newDeploymentFiles)
            ) {
              if (newDeploymentFiles?.length) {
                newDeploymentStatus[message.id] = newDeploymentFiles;
              }
            } else if (oldDeploymentFiles) {
              newDeploymentStatus[message.id] = oldDeploymentFiles;
            }
          }

          return {
            messages,
            versionInformation: generateVersionInformationFromMessages(
              messages,
              {},
            ),
            deploymentStatus: newDeploymentStatus,
            isLoading: false,
          };
        }),

      resetMessages: () =>
        set({
          messages: EMPTY_MESSAGES,
          versionInformation: {},
          deploymentStatus: {},
          isLoading: false,
        }),

      addMessage: (message: FpMessage) =>
        set((state) => {
          // Check if message already exists
          const existingIndex = state.messages.findIndex(
            (msg) => msg.id === message.id,
          );

          // This is typically when the user sends a message and it gets returned from the server
          if (existingIndex >= 0) {
            // Call update message
            state.updateMessage(message.id, message);
            return state;
          }

          // Add new message with appropriate status
          const newMessage: FpMessage = {
            ...message,
            status: message.sender === "user" ? "completed" : message.status,
          };

          const newMessages = [...state.messages, newMessage];

          // Update versions if needed
          const versionInformation = message.version
            ? generateVersionInformationFromMessages(
                newMessages,
                state.versionInformation,
              )
            : state.versionInformation;

          // Update deployment status only if it has changed
          const newDeploymentStatus = { ...state.deploymentStatus };
          const oldDeploymentFiles = state.deploymentStatus[message.id];
          const newDeploymentFiles = getDeploymentFiles(newMessage);

          if (
            hasDeploymentStatusChanged(oldDeploymentFiles, newDeploymentFiles)
          ) {
            if (newDeploymentFiles?.length) {
              newDeploymentStatus[message.id] = newDeploymentFiles;
            } else {
              delete newDeploymentStatus[message.id];
            }
          }

          return {
            messages: newMessages,
            versionInformation,
            deploymentStatus: newDeploymentStatus,
          };
        }),

      appendMessageContent: (id: string, content: string) =>
        set((state) => {
          const updatedMessages = state.messages.map((msg) =>
            msg.id === id ? { ...msg, content: msg.content + content } : msg,
          );
          // No version info changes in content updates, so keep existing versions
          return {
            messages: updatedMessages,
            versionInformation: state.versionInformation,
          };
        }),

      addMessagePart: (messageId: string, part: FpMessagePart, index: number) =>
        set((state) => {
          const oldMessage = state.messages.find((msg) => msg.id === messageId);
          if (!oldMessage) {
            return state;
          }

          const updatedMessages = state.messages.map((msg) => {
            if (msg.id !== messageId) {
              return msg;
            }

            // Initialize parts array if it doesn't exist
            const parts = msg.parts ? [...msg.parts] : [];

            // Create a new array with the correct length
            const newParts = new Array(Math.max(parts.length, index + 1));

            // Copy existing parts
            parts.forEach((p, i) => {
              newParts[i] = p;
            });

            // Insert the new part at the specified index
            newParts[index] = part;

            // Filter out any undefined entries
            const filteredParts = newParts.filter(
              (p): p is FpMessagePart => p !== undefined,
            );

            return { ...msg, parts: filteredParts };
          });

          const newMessage = updatedMessages.find(
            (msg) => msg.id === messageId,
          );
          if (!newMessage) {
            return state;
          }

          // Only update versions if the version info has changed
          const versionInformation = hasVersionChanged(
            oldMessage.version,
            newMessage.version,
          )
            ? generateVersionInformationFromMessages(
                updatedMessages,
                state.versionInformation,
              )
            : state.versionInformation;

          // Update deployment status only if it has changed
          const newDeploymentStatus = { ...state.deploymentStatus };
          const oldDeploymentFiles = state.deploymentStatus[messageId];
          const newDeploymentFiles = getDeploymentFiles(newMessage);

          if (
            hasDeploymentStatusChanged(
              oldDeploymentFiles,
              newDeploymentFiles,
            ) ||
            (newMessage.status === "cancelled" &&
              oldMessage.status !== newMessage.status)
          ) {
            if (newDeploymentFiles?.length) {
              newDeploymentStatus[messageId] = newDeploymentFiles;
            } else {
              delete newDeploymentStatus[messageId];
            }
          }

          return {
            messages: updatedMessages,
            versionInformation,
            deploymentStatus: newDeploymentStatus,
          };
        }),

      setMessageParts: (messageId: string, parts: FpMessagePart[]) =>
        set((state) => {
          const oldMessage = state.messages.find((msg) => msg.id === messageId);
          if (!oldMessage) {
            return state;
          }

          const updatedMessages = state.messages.map((msg) =>
            msg.id === messageId ? { ...msg, parts } : msg,
          );

          const newMessage = updatedMessages.find(
            (msg) => msg.id === messageId,
          );
          if (!newMessage) {
            return state;
          }

          // Only update versions if the version info has changed
          const versionInformation = hasVersionChanged(
            oldMessage.version,
            newMessage.version,
          )
            ? generateVersionInformationFromMessages(
                updatedMessages,
                state.versionInformation,
              )
            : state.versionInformation;

          // Update deployment status only if it has changed
          const newDeploymentStatus = { ...state.deploymentStatus };
          const oldDeploymentFiles = state.deploymentStatus[messageId];
          const newDeploymentFiles = getDeploymentFiles(newMessage);

          if (
            hasDeploymentStatusChanged(oldDeploymentFiles, newDeploymentFiles)
          ) {
            if (newDeploymentFiles?.length) {
              newDeploymentStatus[messageId] = newDeploymentFiles;
            } else {
              delete newDeploymentStatus[messageId];
            }
          }

          return {
            messages: updatedMessages,
            versionInformation,
            deploymentStatus: newDeploymentStatus,
          };
        }),

      updateMessagePart: (
        messageId: string,
        partId: string,
        updates: {
          content?: string;
          status?: string;
          result?: unknown;
        },
      ) =>
        set((state) => {
          const oldMessage = state.messages.find(
            (m: FpMessage) => m.id === messageId,
          );
          if (!oldMessage) {
            return state;
          }

          let newVersion: FpVersion | undefined;
          const updatedParts = oldMessage.parts?.map((part: FpMessagePart) => {
            // HACK - I haven't tracked down why, it's usually during failure modes when a message is interrupted for whatever reason, but sometimes parts lack IDs on the frontend (fixed after refresh)
            if (!part?.id) {
              console.error(
                "[message-store] updateMessagePart: part has no id",
                { part },
              );
              return part;
            }

            if (part.id !== partId) {
              return part;
            }
            // Only update allowed fields for each part type
            if (isTextPart(part)) {
              // Update text part content and/or status
              const updatedPart = { ...part };
              if (updates.content !== undefined) {
                updatedPart.content = updatedPart.content + updates.content;
              }
              if (updates.status !== undefined) {
                updatedPart.status = updates.status as "streaming" | "complete";
              }
              return updatedPart;
            }

            if (isToolCallPart(part)) {
              const updatedPart = { ...part };
              if (updates.status) {
                updatedPart.status = updates.status as
                  | "pending"
                  | "executing"
                  | "complete"
                  | "error";
              }
              if ("result" in updates) {
                updatedPart.result = updates.result;

                if (typeof updates === "object" && "result" in updates) {
                  updatedPart.result = updates.result;

                  const fileLike = FileResultSuccessSchema.safeParse(
                    updates.result,
                  );

                  if (fileLike.success) {
                    const resultData = fileLike.data.data;

                    const file: FpFile = {
                      id: nanoid(), // You'll need to import nanoid
                      filename: resultData.path,
                      content: resultData.content,
                      status: "completed",
                      createdAt: new Date(),
                      updatedAt: new Date(),
                    };

                    const isSpec = resultData.path.endsWith(SPEC_FILE_NAME);

                    // Create a new version or update existing one
                    const currentVersion = oldMessage.version || newVersion;
                    const newFiles = isSpec
                      ? // If it is a spec use the current files list or an empty array
                        (currentVersion?.files ?? [])
                      : // Otherwise filter out the file and add the new one
                        [
                          ...// Use the current files list or an empty array
                          (currentVersion?.files ?? []).filter(
                            (f) => f.filename !== file.filename,
                          ),
                          file,
                        ];

                    const version = {
                      id: currentVersion?.id ?? EPHEMERAL_VERSION_KEY,
                      version: currentVersion?.version ?? 1,
                      files: newFiles,
                      specId:
                        oldMessage.spec?.id ?? currentVersion?.specId ?? "",
                      createdAt: currentVersion?.createdAt ?? new Date(),
                      updatedAt: new Date(),
                    } satisfies FpVersion;

                    // Update the message with the new version
                    newVersion = version;
                  }
                }
              }
              return updatedPart;
            }

            return part;
          });

          const newMessage = {
            ...oldMessage,
            version: newVersion ?? oldMessage.version,
            parts: updatedParts,
          };

          const newMessages = state.messages.map((m: FpMessage) =>
            m.id === messageId ? newMessage : m,
          );

          // Only update versions if the version info has changed
          const versions =
            hasVersionChanged(oldMessage.version, newMessage.version) ||
            oldMessage.spec?.id !== newMessage.spec?.id ||
            (newMessage.status === "cancelled" &&
              oldMessage.status !== newMessage.status)
              ? generateVersionInformationFromMessages(
                  newMessages,
                  state.versionInformation,
                )
              : state.versionInformation;

          // Update deployment status only if it has changed
          const newDeploymentStatus = { ...state.deploymentStatus };
          const oldDeploymentFiles = state.deploymentStatus[messageId];
          const newDeploymentFiles = getDeploymentFiles(newMessage);

          if (
            hasDeploymentStatusChanged(
              oldDeploymentFiles,
              newDeploymentFiles,
            ) ||
            (newMessage.status === "cancelled" &&
              oldMessage.status !== newMessage.status)
          ) {
            if (newDeploymentFiles?.length) {
              newDeploymentStatus[messageId] = newDeploymentFiles;
            } else {
              // If the message is cancelled, we don't want to keep the deployment status
              // TODO: We should try to figure out if there are previous deployment statuses
              // that we can use.
              delete newDeploymentStatus[messageId];
            }
          }

          return {
            messages: newMessages,
            versionInformation: versions,
            deploymentStatus: newDeploymentStatus,
          };
        }),

      setError: (error: string | null) => set({ error }),

      setLoading: (isLoading: boolean) => set({ isLoading }),

      clearMessages: () =>
        set({
          messages: EMPTY_MESSAGES,
          versionInformation: {},
          deploymentStatus: {},
          isLoading: false,
        }),
    }),
    {
      name: "message-store",
    },
  ),
);
